import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { SearchResultsTableEntryData, WrappedEntry } from "@app2/search/search-shared/search-results-table.component";
import { NotificationsService } from "@app2/shared/services/notifications.service";
import { TimeService } from "@app2/shared/services/time.service";
import { WindowService } from "@app2/shared/services/window.service";
import { LongDatePipe } from "@app2/shared/pipes/long-date.pipe";
import { UserService } from "@app2/account/user.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { ModalService } from "@app2/shared/modal/modal.service";
import { AddIndicatorModalComponent } from "@app2/search/search-shared/add-indicator-modal.component";

@UntilDestroy()
@Component({
    selector: "search-results-table-detailed-content",
    template: `
        <table class="results">
            <tbody>
            <tr class="expanded"
                *ngFor="let datum of displayableData">
                <ng-container *ngIf="shouldShowField(datum.key)">
                    <td [class]="getRowColorClassBasedOnSimilarity(datum)"
                        class="expandable-field-column"
                        [attr.test-class]="datum.key + '-field-column'">
                        <ng-container *ngIf="isFilterable(datum.key) && filterable; else notFilterable">
                            <a (click)="toggleFilter.emit({ field: datum.key, value: datum.value })">
                                {{ datum.key }}
                            </a>
                        </ng-container>
                        <ng-template #notFilterable>
                            <span [class.important-field-key]="entry.isFraudDomain && datum.important">
                                {{ datum.key }}
                            </span>
                        </ng-template>
                    </td>
                    <td *ngIf="isNormalField(datum.key)"
                        [class]="getRowColorClassBasedOnSimilarity(datum)"
                        [attr.test-class]="datum.key + '-value-column'"
                        class="data"
                        width="75%">
                        <div *ngFor="let value of datum.value"
                             class="expandable-value-item">
                            <search-results-table-detailed-value *ngIf="datum.key !== 'http_uri'"
                                                                 [text]="strip(value)"
                                                                 [key]="datum.key"
                                                                 [eventTime]="entry.timestamp">
                            </search-results-table-detailed-value>
                            <url-dropdown *ngIf="datum.key === 'http_uri'"
                                          [url]="value"
                                          [eventTime]="entry.timestamp">
                            </url-dropdown>
                            <i *ngIf="value && copyable && datum.key !== 'timestamp'"
                               class="far fa-clone hover-icon margin-right"
                               (click)="copyText(value)"></i>
                            <ng-container *featureGate="'addToWatchlistFromSearchTable'">
                                <ng-container *dsPermissionsRequired="'THREATMATCH_EDIT'">
                                    <i *ngIf="value && datum.key !== 'timestamp'"
                                       [matTooltip]="'ADD_TO_WATCHLIST' | localize"
                                       class="fas fa-plus hover-icon"
                                       (click)="openAddIndicatorModal(value)"></i>
                                </ng-container>
                            </ng-container>
                            <pr-dropdown *ngIf="value && datum.key === 'timestamp'"
                                         test-id="timestamp-copy-dropdown">
                                <div pr-dropdown-trigger class="button-dropdown">
                                    <i class="far fa-clone hover-icon"></i>
                                </div>
                                <ul pr-dropdown-body>
                                    <li (click)="copyAsReadableDate(value)">
                                        {{ 'COPY_HUMAN_READABLE' | localize }}
                                    </li>
                                    <li (click)="copyText(value)">
                                        {{ 'COPY_UNIX' | localize }}
                                    </li>
                                </ul>
                            </pr-dropdown>
                        </div>
                    </td>
                    <td *ngIf="datum.key === 'duration'"
                        [class]="getRowColorClassBasedOnSimilarity(datum)"
                        class="data">
                        <span>{{ datum.value }}</span>
                        <span *ngIf="getField(entry)" class="de-emphasize">
                            ({{ getField(entry).value }})
                        </span>
                    </td>
                </ng-container>
            </tr>
            <tr *ngIf="showFieldsDropdown" class="show-more-fields-row">
                <ds-button type="blue-link"
                           (clicked)="toggleAllFields()"
                           test-id="show-more-fields-button">
                    <i class="far dropdown"
                       [ngClass]="{'fa-chevron-up': showAllFields, 'fa-chevron-down': !showAllFields}"></i>
                    {{ (showAllFields ? 'SHOW_LESS_FIELDS' : 'SHOW_MORE_FIELDS') | localize }}
                </ds-button>
            </tr>
            </tbody>
        </table>
    `,
    styles: [`
        @import "/src/styles/colors";
        @import "/src/styles/settings";

        .important-field-key {
            color: $gray-9;
            font-weight: $font-weight-demibold;
        }

        .show-more-fields-row:hover {
            background-color: unset;
        }
    `],
})
export class SearchResultsTableDetailedContentComponent implements OnInit, OnChanges {
    @Input() entry: WrappedEntry;
    @Input() filterable: boolean;
    @Input() copyable: boolean;
    @Input() simdifEnabled: boolean = false;
    @Input() anchorEventSet: Set<any>;

    @Output() toggleFilter = new EventEmitter<any>();

    showAllFields: boolean;
    showFieldsDropdown: boolean;
    isAdmin: boolean;
    allData: SearchResultsTableEntryData[] = [];
    displayableData: SearchResultsTableEntryData[] = [];

    constructor(private readonly userService: UserService,
                private notificationsService: NotificationsService,
                private timeService: TimeService,
                private windowService: WindowService,
                private modalService: ModalService) {
    }

    ngOnInit() {
        this.userService.hasPermission$("PR_ADMIN")
            .pipe(untilDestroyed(this))
            .subscribe(isAdmin => this.isAdmin = isAdmin);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.entry && this.entry.data) {
            this.allData = this.entry.data;
            this.showFieldsDropdown = this.allData[0].important && this.entry.isFraudDomain;
            this.displayableData = this.showFieldsDropdown ? this.allData.filter(d => d.important) : this.allData;
        }
    }

    shouldShowField(key: string): boolean {
        return !(key === "duration_unit" || (key === "praesidio_parse_nanos" && !this.isAdmin));
    }

    getRowColorClassBasedOnSimilarity(datum: SearchResultsTableEntryData) {
        if (!this.simdifEnabled) return "";

        return this.anchorEventSet.has(this.getKeyValueAsString(datum)) ? "similar-field-value-row" : "different-field-value-row";
    }

    getField(entry: WrappedEntry): object {
        const dataEntriesWithDurationUnit = entry.data.filter(item => Object.keys(item).includes("duration_unit"));
        return dataEntriesWithDurationUnit.length > 0 ? dataEntriesWithDurationUnit[0] : null;
    }

    isNormalField(key: string): boolean {
        return key !== "duration" && key !== "duration_unit";
    }

    /*
     * Strip curly braces from the value that we are going to pass to formatted-html.
     * Formatted-Html can accidentally bind arbitrary text with multiple matched curly braces, which causes the
     * Angular1 lexer to attempt to evaluate it.
     * Value can be type cast as number or string, possibly any other typing we allow from ES field-whitelist in
     * ingestion as well, so interpolate for string value.
     */
    strip(value?: any): string | undefined {
        return value === undefined ? undefined : `${ value }`.replace(/({{)|(}})/g, "");
    }

    copyText(value: any): void {
        this.windowService.copyToClipboard(this.strip(value));
        this.notificationsService.showSuccess("Copied to Clipboard");
    }

    copyAsReadableDate(value: any): void {
        const pipe = new LongDatePipe(this.timeService);
        const readableDate = pipe.transform(value);
        this.copyText(readableDate);
    }

    getKeyValueAsString(entry: SearchResultsTableEntryData): string {
        return entry.key + "-" + entry.value;
    }

    isFilterable(fieldName: string): boolean {
        const unfilterableFieldNames = ["subject", "response"];
        const unfilterableSuffixes = ["message", "location"];

        return fieldName !== undefined && !(unfilterableFieldNames.includes(fieldName) ||
            unfilterableSuffixes.some((suffix) => fieldName.endsWith(suffix)));
    }

    toggleAllFields() {
        this.showAllFields = !this.showAllFields;
        this.displayableData = this.displayableData.length === this.allData.length ? this.allData.filter(d => d.important) : this.allData;
    }

    openAddIndicatorModal(value: string) {
        // when this closes, it emits the id of the watchlist that should be refreshed. this is done in the modal itself
        // because including watchlistService in this component would make the app request watchlist data when only
        // viewing the search results table which doesn't need it
        this.modalService.open(AddIndicatorModalComponent, { indicator: value });
    }
}
